import * as React from "react";
import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ExternalTrackRowContainer,
  TrackHeader,
  StemsList,
  StemItem,
  RegionTimeline,
  RegionBlock
} from "./styles";
import {
  LOAD_EXTERNAL_LANE,
  LoadExternalLaneAction
} from "../../actions/actionTypes";
import { InstrumentCategories, MainStore, Lane } from "../../store";
import { getInstrumentColorCombo } from "../../util/lane_color_helpers";
import { getFile } from "../../api/WebAPI";

// Module-level reference to stop any currently playing stem preview
let currentlyPlayingAudio: {
  audio: HTMLAudioElement;
  stop: () => void;
} | null = null;

const PREVIEW_DURATION_SECONDS = 8;

export interface StemRegion {
  visualStartTime: number;
  visualDuration: number;
}

export interface MatchingStem {
  stemName: string;
  stemMp3File: string;
  stemWavFile?: string;
  instrumentName: string;
  instrumentCategory: string;
  regions?: StemRegion[];
}

export interface MatchingTrackData {
  trackId: string;
  trackTitle: string;
  initialBeat: number;
  trackDuration?: number;
  stems: MatchingStem[];
}

interface ExternalTrackRowProps {
  track: MatchingTrackData;
  trackIndex: number;
  afterAdd: () => void;
}

/*
 * This component displays a matching track as an expandable row with:
 * - Track title with expand/collapse caret
 * - First stem shown inline with the header (colored bubble, instrument name, timeline)
 * - Expanded view shows all stems from that track
 */
export function ExternalTrackRow({
  track,
  trackIndex,
  afterAdd
}: ExternalTrackRowProps) {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingStemIndex, setPlayingStemIndex] = useState<number | null>(null);
  const [playingRegionIndex, setPlayingRegionIndex] = useState<number | null>(
    null
  );
  const [signedAudioUrls, setSignedAudioUrls] = useState<
    Record<string, string>
  >({});
  const audioRef = useRef<HTMLAudioElement>(null);
  const isMountedRef = useRef(true);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    const audioElement = audioRef.current;
    return () => {
      isMountedRef.current = false;
      // Clear any running interval
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      // Stop audio if playing
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      // Clear the global reference if it's ours
      if (currentlyPlayingAudio?.audio === audioElement) {
        currentlyPlayingAudio = null;
      }
    };
  }, []);

  // Count existing lanes in each instrument category to determine color index
  const lanes = useSelector<MainStore, Lane[]>(state =>
    Object.values(state.project.lanes.byId)
  );

  const hasMultipleStems = track.stems.length > 1;
  const firstStem = track.stems[0];

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMultipleStems) {
      setIsExpanded(!isExpanded);
    }
  };

  const getColorForStem = (stem: MatchingStem, stemIndex: number) => {
    const numInCategory = lanes.filter(
      lane => lane.instrument.category === stem.instrumentCategory
    ).length;
    return getInstrumentColorCombo(
      stem.instrumentCategory as InstrumentCategories,
      numInCategory + trackIndex + stemIndex
    )[0];
  };

  const addExternalLane = (stem: MatchingStem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    dispatch<LoadExternalLaneAction>({
      type: LOAD_EXTERNAL_LANE,
      stemName: stem.stemName,
      stemMp3File: stem.stemMp3File,
      stemWavFile: stem.stemWavFile,
      instrumentName: stem.instrumentName,
      instrumentCategory: stem.instrumentCategory,
      trackTitle: track.trackTitle,
      initialBeat: track.initialBeat
    });
    afterAdd();
  };

  const handleRegionClick = useCallback(
    async (
      stem: MatchingStem,
      stemIndex: number,
      region: StemRegion,
      regionIndex: number,
      e: React.MouseEvent
    ) => {
      e.stopPropagation();

      const audio = audioRef.current;
      if (!audio) return;

      // If clicking the same region that's playing, stop it
      if (
        isPlaying &&
        playingStemIndex === stemIndex &&
        playingRegionIndex === regionIndex
      ) {
        audio.pause();
        audio.currentTime = 0;
        setIsPlaying(false);
        setPlayingStemIndex(null);
        setPlayingRegionIndex(null);
        currentlyPlayingAudio = null;
        return;
      }

      // Stop any currently playing stem preview
      if (currentlyPlayingAudio) {
        currentlyPlayingAudio.stop();
      }

      setPlayingStemIndex(stemIndex);
      setPlayingRegionIndex(regionIndex);
      setIsPlaying(true);

      try {
        // Get a signed URL for the audio file if we don't have one yet
        let audioUrl = signedAudioUrls[stem.stemMp3File];
        if (!audioUrl) {
          audioUrl = await getFile(stem.stemMp3File);
          // Check if still mounted before updating state
          if (!isMountedRef.current) return;
          setSignedAudioUrls(prev => ({
            ...prev,
            [stem.stemMp3File]: audioUrl
          }));
        }

        // Check if still mounted before continuing
        if (!isMountedRef.current) return;

        audio.src = audioUrl;
        audio.load();

        await new Promise<void>((resolve, reject) => {
          const onCanPlay = () => {
            audio.removeEventListener("canplay", onCanPlay);
            audio.removeEventListener("error", onError);
            resolve();
          };
          const onError = () => {
            audio.removeEventListener("canplay", onCanPlay);
            audio.removeEventListener("error", onError);
            reject(new Error("Failed to load audio"));
          };
          audio.addEventListener("canplay", onCanPlay);
          audio.addEventListener("error", onError);
        });

        // Check if still mounted before playing
        if (!isMountedRef.current) return;

        audio.currentTime = region.visualStartTime;
        await audio.play();

        const endTime = Math.min(
          region.visualStartTime + PREVIEW_DURATION_SECONDS,
          region.visualStartTime + region.visualDuration
        );

        const stopPlayback = () => {
          audio.pause();
          // Only update state if still mounted
          if (isMountedRef.current) {
            setIsPlaying(false);
            setPlayingStemIndex(null);
            setPlayingRegionIndex(null);
          }
          currentlyPlayingAudio = null;
          // Clear the interval ref
          if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
          }
        };

        currentlyPlayingAudio = { audio, stop: stopPlayback };

        // Clear any previous interval
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
        }

        checkIntervalRef.current = setInterval(() => {
          if (audio.currentTime >= endTime || audio.paused) {
            stopPlayback();
          }
        }, 100);

        audio.onended = () => {
          stopPlayback();
        };
      } catch (error) {
        console.error("Error playing audio preview:", error);
        // Only update state if still mounted
        if (isMountedRef.current) {
          setIsPlaying(false);
          setPlayingStemIndex(null);
          setPlayingRegionIndex(null);
        }
      }
    },
    [isPlaying, playingStemIndex, playingRegionIndex, signedAudioUrls]
  );

  const handleAudioError = () => {
    setIsPlaying(false);
    setPlayingStemIndex(null);
    setPlayingRegionIndex(null);
  };

  const renderStemTimeline = (
    stem: MatchingStem,
    stemIndex: number,
    bubbleColor: string
  ) => {
    // Calculate duration from this stem's regions or use track duration
    const stemMaxTime =
      stem.regions?.reduce(
        (max, r) => Math.max(max, r.visualStartTime + r.visualDuration),
        0
      ) || 0;
    const duration = track.trackDuration || stemMaxTime || 60;

    return (
      <RegionTimeline className="region-timeline">
        {stem.regions && stem.regions.length > 0 ? (
          stem.regions.map((region, regionIndex) => {
            const leftPercent = Math.min(
              (region.visualStartTime / duration) * 100,
              100
            );
            const widthPercent = Math.min(
              (region.visualDuration / duration) * 100,
              100 - leftPercent
            );

            return (
              <RegionBlock
                key={regionIndex}
                color={bubbleColor}
                style={{
                  left: `${leftPercent}%`,
                  width: `${Math.max(widthPercent, 1)}%`
                }}
                onClick={e =>
                  handleRegionClick(stem, stemIndex, region, regionIndex, e)
                }
                isPlaying={
                  playingStemIndex === stemIndex &&
                  playingRegionIndex === regionIndex
                }
                title={`Click to preview (${region.visualStartTime.toFixed(
                  1
                )}s - ${(
                  region.visualStartTime + region.visualDuration
                ).toFixed(1)}s)`}
              />
            );
          })
        ) : (
          <div className="no-regions">No region data</div>
        )}
      </RegionTimeline>
    );
  };

  const firstStemColor = getColorForStem(firstStem, 0);

  return (
    <ExternalTrackRowContainer>
      <TrackHeader
        hasMultipleStems={hasMultipleStems}
        bubbleColor={firstStemColor}
      >
        {/* Track title section with caret - hover reveals caret and count */}
        <div className="track-title-section" onClick={toggleExpanded}>
          <div
            className={`caret-container ${
              hasMultipleStems ? "clickable" : "hidden"
            }`}
          >
            <svg
              className={`caret-icon ${isExpanded ? "expanded" : ""}`}
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
            >
              <path
                d="M3 1.5L7 5L3 8.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="track-title">
            <span className="track-name">{track.trackTitle}</span>
            {hasMultipleStems && (
              <span className="stem-count">({track.stems.length})</span>
            )}
          </div>
        </div>

        {/* Stem row content with background */}
        <div className="stem-row-content">
          {/* First stem inline display */}
          <div
            className="stem-info"
            onClick={e => addExternalLane(firstStem, e)}
            title={`Add ${firstStem.instrumentName} from "${track.trackTitle}"`}
          >
            <div className="stem-color-bubble">
              <svg
                className="plus-icon"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <path
                  d="M5 1V9M1 5H9"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="instrument-name">{firstStem.instrumentName}</div>
          </div>

          {/* First stem timeline */}
          {renderStemTimeline(firstStem, 0, firstStemColor)}
        </div>
      </TrackHeader>

      {/* Expanded stems list */}
      {isExpanded && hasMultipleStems && (
        <StemsList>
          {track.stems.slice(1).map((stem, idx) => {
            const stemIndex = idx + 1;
            const bubbleColor = getColorForStem(stem, stemIndex);

            return (
              <StemItem
                key={`${stem.stemName}-${stemIndex}`}
                bubbleColor={bubbleColor}
              >
                {/* Track title spacer */}
                <div className="track-title-spacer" />

                {/* Stem row content with background */}
                <div className="stem-row-content">
                  {/* Stem info */}
                  <div
                    className="stem-info"
                    onClick={e => addExternalLane(stem, e)}
                    title={`Add ${stem.instrumentName} from "${track.trackTitle}"`}
                  >
                    <div className="stem-color-bubble">
                      <svg
                        className="plus-icon"
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        fill="none"
                      >
                        <path
                          d="M5 1V9M1 5H9"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div className="instrument-name">{stem.instrumentName}</div>
                  </div>

                  {/* Stem timeline */}
                  {renderStemTimeline(stem, stemIndex, bubbleColor)}
                </div>
              </StemItem>
            );
          })}
        </StemsList>
      )}

      {/* Hidden audio element for preview playback */}
      <audio ref={audioRef} preload="none" onError={handleAudioError} />
    </ExternalTrackRowContainer>
  );
}
